<script setup lang="ts">
import { TriangleAlert, X } from '@lucide/vue'
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue-sonner'

import { Button } from '@/components/ui/button/index.ts'

import { useImpersonation } from '../composables/useImpersonation'

const { t } = useI18n()
const { isImpersonating, target, isLoading, stop, initFromPage } = useImpersonation()

// Initialize from page props on mount
onMounted(() => {
  initFromPage()
  updateBodyClass(isImpersonating.value)
})

// Watch for changes and update body class
watch(isImpersonating, (value) => {
  updateBodyClass(value)
})

// Cleanup on unmount
onUnmounted(() => {
  updateBodyClass(false)
})

/**
 * Display name with fallback chain
 */
const targetName = computed(() => {
  if (!target.value) return t('portal.impersonation.unknown_user')
  return target.value.name ?? target.value.email ?? t('portal.impersonation.unknown_user')
})

/**
 * Handle stop button click
 */
const handleStop = async () => {
  try {
    await stop()
    toast.success(t('portal.impersonation.stopped'))
  } catch {
    toast.error(t('portal.impersonation.stop_error'))
  }
}

/**
 * Update body class and CSS variable for layout displacement
 */
function updateBodyClass(isActive: boolean) {
  const body = document.body
  const bannerHeight = '40px'

  if (isActive) {
    body.classList.add('body--impersonating')
    body.style.setProperty('--portal-banner-height', bannerHeight)
  } else {
    body.classList.remove('body--impersonating')
    body.style.removeProperty('--portal-banner-height')
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-transform duration-300 ease-out"
      enter-from-class="-translate-y-full"
      enter-to-class="translate-y-0"
      leave-active-class="transition-transform duration-200 ease-in"
      leave-from-class="translate-y-0"
      leave-to-class="-translate-y-full"
    >
      <div
        v-if="isImpersonating"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        class="fixed left-0 right-0 top-0 z-[100] flex h-10 items-center justify-center gap-3 bg-amber-500 px-4 text-sm font-medium text-amber-950"
      >
        <TriangleAlert class="h-4 w-4 flex-shrink-0" aria-hidden="true" />

        <span>
          {{ t('portal.impersonation.banner_message', { name: targetName }) }}
        </span>

        <Button
          variant="ghost"
          size="sm"
          :disabled="isLoading"
          class="ml-2 h-7 gap-1.5 bg-transparent text-amber-950 hover:bg-amber-600 hover:text-amber-950"
          @click="handleStop"
        >
          <X class="h-3.5 w-3.5" aria-hidden="true" />
          <span>{{ t('portal.impersonation.stop') }}</span>
          <span class="sr-only">{{ t('portal.impersonation.stop') }}</span>
        </Button>
      </div>
    </Transition>
  </Teleport>
</template>
